import API from '@/shared/plugins/Api/API'

const DEFAULT_DEPTH = 2

class TablesClient extends API {
  // Table-level lineage graph around a table: table + job nodes with
  // `produces`/`consumes` edges. Table nodes embed their columns.
  // `depth` applies both upstream and downstream. `fullGraph` also includes
  // sibling tables (sent to the BE as `direction`: 'all' vs 'flow').
  async get (tableId, options = {}) {
    return this.request({
      method: 'GET',
      url: `lineage/v1/tables/${tableId}`,
      params: {
        depth: Number.isFinite(options.depth) ? options.depth : DEFAULT_DEPTH,
        direction: options.fullGraph ? 'all' : 'flow'
      }
    })
  }

  // Column-level lineage as an id-keyed `edges` map (each `column-transform`
  // edge stored once, metadata carries the transformation type/description
  // and the producing job id) plus `slices` keyed by the anchor table's
  // column names: `slices[name].edges` lists the edge ids on that column's
  // own up/downstream path (directional walk, `depth` bounds each direction).
  // No nodes are returned — the FE derives the highlighted column ids
  // ("{table_id}::{column_name}") from the anchor + the slice edges'
  // endpoints.
  async getColumns (tableId, options = {}) {
    return this.request({
      method: 'GET',
      url: `lineage/v1/tables/${tableId}/columns`,
      params: {
        depth: Number.isFinite(options.depth) ? options.depth : DEFAULT_DEPTH
      }
    })
  }
}

class JobsClient extends API {
  // Job detail: name, kind, run info, `job_details`, and the input/output
  // tables as ids only (input_table_ids / output_table_ids) — table names
  // are resolved FE-side against the loaded graph.
  async getDetail (jobId) {
    const job = await this.request({
      method: 'GET',
      url: `lineage/v1/jobs/${encodeURIComponent(jobId)}`
    })
    // Guarantee `id` (panel header and the store's stale-guard key on it).
    return { id: job._id || jobId, ...job }
  }
}

class LINEAGE extends API {
  constructor (id) {
    super(id)
    this.tables = new TablesClient(id)
    this.jobs = new JobsClient(id)
  }
}

export default LINEAGE
